/* eslint-disable */
import React, { useState } from 'react';
import { X, Upload, Trash2, CheckCircle2 } from 'lucide-react';
import StarPicker from './StarPicker';
import { filesToCompressedDataUrls } from '../../lib/imageUpload';
import { reviewsApi } from '../../lib/api';

const ReviewForm = ({ productId, onClose, onSubmitted }) => {
  const [rating, setRating] = useState(5.0);
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [images, setImages] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setErr('');
    try {
      const remaining = 5 - images.length;
      const list = Array.from(files).slice(0, remaining);
      const urls = await filesToCompressedDataUrls(list);
      setImages((cur) => [...cur, ...urls].slice(0, 5));
    } catch (ex) {
      setErr(ex.message || 'Upload failed');
    } finally {
      e.target.value = '';
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !text.trim()) {
      setErr('Please add a title and a review.');
      return;
    }
    setBusy(true); setErr('');
    try {
      const review = await reviewsApi.create(productId, { rating, title, text, images });
      onSubmitted?.(review);
      setSubmitted(true);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || 'Could not submit review.');
    } finally {
      setBusy(false);
    }
  };

  if (submitted) {
    return (
      <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
        <div className="bg-[#f5f1e6] w-full max-w-xl">
          <div className="flex items-center justify-between px-6 h-16 border-b border-[#e5dfd0]">
            <div className="text-[11px] tracking-[0.28em]">REVIEW SUBMITTED</div>
            <button onClick={onClose} aria-label="Close confirmation"><X className="w-5 h-5" strokeWidth={1.4} /></button>
          </div>
          <div className="px-8 py-12 text-center">
            <div className="mx-auto w-14 h-14 rounded-full bg-[#ece4d2] flex items-center justify-center">
              <CheckCircle2 className="w-7 h-7 text-[#1a1410]" strokeWidth={1.4} />
            </div>
            <h3 className="font-serif text-3xl italic mt-6 text-[#1a1410]">Thank you.</h3>
            <p className="mt-3 text-[15px] text-[#3a2e26] leading-relaxed max-w-md mx-auto">
              Your review has been submitted and is awaiting approval. It will appear on the product page once our team has reviewed it.
            </p>
            <button onClick={onClose}
              className="mt-10 bg-[#1a1410] text-[#f5f1e6] px-8 py-3 text-[11px] tracking-[0.32em] hover:bg-black transition-colors">
              CLOSE
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-[#f5f1e6] w-full max-w-xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 h-16 border-b border-[#e5dfd0]">
          <div className="text-[11px] tracking-[0.28em]">SHARE YOUR REVIEW</div>
          <button onClick={onClose} aria-label="Close review form"><X className="w-5 h-5" strokeWidth={1.4} /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-6">
          <div>
            <label className="text-[10px] tracking-[0.22em] text-[#7a6a5a]">YOUR RATING</label>
            <div className="mt-3">
              <StarPicker value={rating} onChange={setRating} />
            </div>
          </div>
          <div>
            <label className="text-[10px] tracking-[0.22em] text-[#7a6a5a]">REVIEW TITLE</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} required maxLength={200}
              placeholder="Sum up your experience"
              className="mt-2 w-full bg-transparent border-b border-[#cdc2b1] focus:border-[#1a1410] py-2 focus:outline-none" />
          </div>
          <div>
            <label className="text-[10px] tracking-[0.22em] text-[#7a6a5a]">YOUR REVIEW</label>
            <textarea value={text} onChange={(e) => setText(e.target.value)} required maxLength={5000} rows={5}
              placeholder="Tell us what you loved… wear, finish, longevity, comfort."
              className="mt-2 w-full bg-transparent border border-[#cdc2b1] focus:border-[#1a1410] p-3 text-sm focus:outline-none" />
            <div className="text-[10px] text-[#7a6a5a] mt-1">{text.length}/5000</div>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <label className="text-[10px] tracking-[0.22em] text-[#7a6a5a]">PHOTOS (up to 5)</label>
              <label className={`inline-flex items-center gap-2 text-[11px] tracking-[0.22em] cursor-pointer hover:text-black ${images.length >= 5 ? 'opacity-40 pointer-events-none' : ''}`}>
                <Upload className="w-4 h-4" strokeWidth={1.4} /> ADD PHOTOS
                <input type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" onChange={handleUpload} disabled={images.length >= 5} />
              </label>
            </div>
            {images.length > 0 && (
              <div className="mt-3 grid grid-cols-5 gap-2">
                {images.map((src, idx) => (
                  <div key={idx} className="relative aspect-square border border-[#cdc2b1] overflow-hidden group">
                    <img src={src} alt={`Review upload ${idx + 1}`} className="w-full h-full object-cover" />
                    <button type="button" onClick={() => setImages((cur) => cur.filter((_, i) => i !== idx))}
                      className="absolute top-1 right-1 bg-[#1a1410] text-[#f5f1e6] p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Trash2 className="w-3 h-3" strokeWidth={1.6} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {err && <div className="p-3 bg-[#f3e3d8] border border-[#cdc2b1] text-sm text-[#7a3a2a]">{err}</div>}

          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={busy} className="flex-1 bg-[#1a1410] text-[#f5f1e6] py-3 text-[11px] tracking-[0.32em] hover:bg-black disabled:opacity-50">
              {busy ? 'PUBLISHING…' : 'PUBLISH REVIEW'}
            </button>
            <button type="button" onClick={onClose} className="px-6 py-3 text-[11px] tracking-[0.32em] border border-[#cdc2b1]">CANCEL</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReviewForm;
